// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching and managing assets inventory data.
 * This module provides functionality for requesting and caching assets
 * that are NOT currently in inventory from the remote API using React Query.
 */
import { getAssetsInventory } from '@/inventory/api/get-assets-inventory';
import { AssetsInventoryResponse } from '@/inventory/api/types';
import {
  normalizeFilters,
  serializeFiltersToKey,
} from '@/inventory/utilities/serialize-filters';
import { THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

/**
 * Specific filter options for asset inventory filtering.
 * Assets and products cannot be filtered simultaneously.
 */
interface AssetInventoryFilters {
  locationId?: string[];
  locationKey?: string[];
  customerId?: string[];
  customerKey?: string[];
  // Asset filtering - mutually exclusive with product filtering
  assetId?: string[];
  assetKey?: string[];
  // Product filtering - mutually exclusive with asset filtering
  productId?: string[];
  productKey?: string[];
  // Include non-inventory locations (customer sites)
  includeNonInventory?: boolean;
}

/**
 * Configuration options for the useAssetsInventory hook.
 */
interface UseAssetsInventoryOptions {
  filters?: AssetInventoryFilters;
  enabled?: boolean;
}

/**
 * Return type for the useAssetsInventory hook.
 */
interface UseAssetsInventoryResult {
  /**
   * The assets inventory response data containing the list of assets NOT in inventory
   */
  assetsInventory?: AssetsInventoryResponse;

  /**
   * Whether the initial data is being loaded
   */
  isLoading: boolean;

  /**
   * Whether a background fetch is in progress
   */
  isFetching: boolean;

  /**
   * Whether the query has encountered an error
   */
  isError: boolean;

  /**
   * The error object if the query failed
   */
  error: Error | null;

  /**
   * Function to manually trigger a refetch
   */
  refetch: UseQueryResult<AssetsInventoryResponse>['refetch'];
}

/**
 * Validates that asset and product filters are not used simultaneously.
 */
function validateFilters(filters: AssetInventoryFilters): void {
  const hasAssetFilters = filters.assetId?.length || filters.assetKey?.length;
  const hasProductFilters =
    filters.productId?.length || filters.productKey?.length;

  if (hasAssetFilters && hasProductFilters) {
    throw new Error('Cannot filter by both assets and products simultaneously');
  }
}

/**
 * Converts AssetInventoryFilters to the generic InventoryFilters format.
 * Note: Only includes fields that are supported by the backend inventory API.
 */
function convertFilters(filters: AssetInventoryFilters) {
  // Validate filters before converting
  validateFilters(filters);

  return {
    locationIds: filters.locationId,
    assetIds: filters.assetId,
    assetKeys: filters.assetKey,
    productKeys: filters.productKey,
    includeNonInventory: filters.includeNonInventory,
  };
}

/**
 * A React hook that fetches and caches assets inventory data from the remote API.
 * This hook specifically fetches assets that are NOT currently in inventory,
 * which includes assets in transit, on projects, or otherwise not available.
 *
 * The hook supports:
 * - Optional filtering by location ID, customer ID, asset ID/key, or product key
 * - Validation that prevents simultaneous asset and product filtering
 * - Automatic background updates and caching via React Query
 * - Manual refetch capability
 * - Loading and error state management
 *
 * @param options - Configuration options for filtering and query behavior
 * @returns Object containing the assets inventory data and query state
 * @throws Error if both asset and product filters are provided simultaneously
 *
 * @example
 * ```tsx
 * // Fetch all assets not in inventory
 * const { assetsInventory, isLoading } = useAssetsInventory();
 *
 * // Fetch assets filtered by location
 * const { assetsInventory, isLoading } = useAssetsInventory({
 *   filters: { locationId: ['loc-123'] }
 * });
 *
 * // Fetch specific assets by ID (valid)
 * const { assetsInventory, isLoading } = useAssetsInventory({
 *   filters: { assetId: ['asset-1', 'asset-2'] }
 * });
 *
 * // Fetch assets by product key (valid)
 * const { assetsInventory, isLoading } = useAssetsInventory({
 *   filters: { productKey: ['product-123'] }
 * });
 *
 * // This would throw an error - cannot filter by both assets and products
 * const { assetsInventory, isLoading } = useAssetsInventory({
 *   filters: {
 *     assetId: ['asset-1'],
 *     productKey: ['product-123'] // ERROR: mutually exclusive
 *   }
 * });
 * ```
 */
export function useAssetsInventory(
  options: UseAssetsInventoryOptions = {}
): UseAssetsInventoryResult {
  const { filters = {}, enabled = true } = options;

  // Convert and validate filters
  const inventoryFilters = convertFilters(filters);

  // Normalize filters and create a stable query key
  const normalizedFilters = normalizeFilters(inventoryFilters);
  const filtersKey = serializeFiltersToKey(normalizedFilters);

  const queryResult = useQuery({
    queryKey: ['inventory', 'assets', filtersKey],
    queryFn: () => getAssetsInventory(normalizedFilters),
    enabled,
    staleTime: 0, // Always stale - refetch immediately on mount/access
    refetchInterval: THIRTY_SECONDS_MS,
    refetchOnWindowFocus: true,
  });

  return {
    assetsInventory: queryResult.data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}
